import { useState, useMemo, useEffect, useRef } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Plus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn, getContrastTextColor } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import { registerView, type ViewProps } from '@/components/workspace/ViewRegistry';
import {
  ListViewToolbar,
  ListViewColumnHeader,
  parseListViewPreferences,
  applyListViewWithTableColumnSort,
  DEFAULT_LIST_VIEW_PREFS,
  type ListViewPreferences,
  type ListViewSortDirection,
  type ListViewTableSortColumn,
  TaskListRow,
} from '@/features/list-view';
import { useEffectiveCustomFieldsForList, useTaskCustomFieldValuesForList, useCreateListCustomField, useUpdateCustomField } from '@/hooks/use-custom-fields';
import { useMyCapabilities } from '@/hooks/use-permissions';
import { useClearViewPreference, useSetViewPreference, useViewPreference } from '@/hooks/use-view-preference';
import { toast } from '@/components/ui/toaster';
import type { Task } from '@/types';
import type { CustomFieldDefinition } from '@/lib/api';

const LIST_VIEW_BASE_COLUMNS = [
  'minmax(300px, 1fr)', // Name + tags + meta
  '140px', // Status
  '160px', // Assignee
  '100px', // Due date
  '100px', // Priority
  '100px', // Last updated
] as const;

function getListViewGridTemplate(customFieldCount: number): string {
  const customColumns = Array.from({ length: customFieldCount }, () => 'minmax(110px, 1fr)');
  return [...LIST_VIEW_BASE_COLUMNS, ...customColumns, '110px'].join(' ');
}

function nextPrefsFromTableSortClick(
  prev: ListViewPreferences,
  column: ListViewTableSortColumn
): ListViewPreferences {
  if (prev.tableSortColumn !== column) {
    return { ...prev, tableSortColumn: column, tableSortDirection: 'asc' };
  }
  if (prev.tableSortDirection === 'asc') {
    return { ...prev, tableSortDirection: 'desc' };
  }
  return { ...prev, tableSortColumn: null, tableSortDirection: 'asc' };
}

function ListViewSortableColumnHeader({
  label,
  column,
  activeColumn,
  direction,
  onClick,
}: {
  label: string;
  column: ListViewTableSortColumn;
  activeColumn: ListViewTableSortColumn | null;
  direction: ListViewSortDirection;
  onClick: (col: ListViewTableSortColumn) => void;
}) {
  const isActive = activeColumn === column;
  return (
    <button
      type="button"
      onClick={() => onClick(column)}
      aria-sort={
        isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      className={cn(
        'inline-flex min-h-8 w-full max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-left font-medium transition-colors',
        'hover:bg-accent/60 hover:text-foreground',
        isActive ? 'text-foreground' : 'text-muted-foreground'
      )}
    >
      <span className="truncate">{label}</span>
      {isActive &&
        (direction === 'asc' ? (
          <ArrowUp className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
        ) : (
          <ArrowDown className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
        ))}
    </button>
  );
}

interface ListViewGroupBlockProps {
  groupKey: string;
  label: string;
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
  visibleCustomFields: CustomFieldDefinition[];
  customValuesByTask: Record<string, Record<string, unknown>>;
  onRemoveColumn: (fieldId: string) => void;
  canManageColumns: boolean;
  onRenameColumn?: (fieldId: string, newName: string) => void;
  canRenameColumn: boolean;
  onAddExistingColumn?: (fieldId: string) => void;
  hiddenEffectiveFields: CustomFieldDefinition[];
  onCreateColumnByType?: (type: 'text' | 'longText' | 'number') => void;
  isAddingColumn: boolean;
  showAddColumn: boolean;
  canCreateFieldDefinition: boolean;
  tableSortColumn: ListViewTableSortColumn | null;
  tableSortDirection: ListViewSortDirection;
  onTableSortColumnClick: (column: ListViewTableSortColumn) => void;
  isExpanded: boolean;
  onToggleExpanded: (groupKey: string) => void;
  /** Status info for rendering as a colored pill when grouping by status */
  status?: { id: string; name: string; color: string } | null;
}

function ListViewGroupBlock({
  groupKey,
  label,
  tasks,
  onTaskClick,
  visibleCustomFields,
  customValuesByTask,
  onRemoveColumn,
  canManageColumns,
  onRenameColumn,
  canRenameColumn,
  onAddExistingColumn,
  hiddenEffectiveFields,
  onCreateColumnByType,
  isAddingColumn,
  showAddColumn,
  canCreateFieldDefinition,
  tableSortColumn,
  tableSortDirection,
  onTableSortColumnClick,
  isExpanded,
  onToggleExpanded,
  status,
}: ListViewGroupBlockProps) {
  const showHeader = !!label;

  const gridCols = getListViewGridTemplate(visibleCustomFields.length);

  // When grouping by status, show total count for that status group
  // When NOT grouping by status, show count excluding done/closed tasks (ClickUp-style)
  const displayCount = status
    ? tasks.length // Status group: show all tasks in this status
    : tasks.filter((t) => !t.status?.isDoneState).length; // Other groupings: exclude done tasks

  return (
    <div className="mb-4">
      {showHeader && (
        <button
          type="button"
          onClick={() => onToggleExpanded(groupKey)}
          className="w-full flex items-center gap-2 px-2 py-2 hover:bg-accent/40 rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          {status ? (
            <span
              className="text-sm font-medium px-2.5 py-0.5 rounded-full"
              style={{
                backgroundColor: status.color,
                color: getContrastTextColor(status.color),
              }}
            >
              {status.name}
            </span>
          ) : (
            <span className="text-sm font-medium truncate">{label}</span>
          )}
          <span className="text-xs text-muted-foreground tabular-nums">{displayCount}</span>
        </button>
      )}
      {(!showHeader || isExpanded) && (
        <div className={showHeader ? 'ml-6' : ''}>
          <div className="overflow-x-auto">
            <div className="min-w-max">
              {(tasks.length > 0 || showAddColumn) && (
                <div
                  style={{ gridTemplateColumns: gridCols }}
                  className="grid items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground border-b border-border"
                >
                  <ListViewSortableColumnHeader
                    label="Name"
                    column="name"
                    activeColumn={tableSortColumn}
                    direction={tableSortDirection}
                    onClick={onTableSortColumnClick}
                  />
                  <ListViewSortableColumnHeader
                    label="Status"
                    column="status"
                    activeColumn={tableSortColumn}
                    direction={tableSortDirection}
                    onClick={onTableSortColumnClick}
                  />
                  <ListViewSortableColumnHeader
                    label="Assignee"
                    column="assignee"
                    activeColumn={tableSortColumn}
                    direction={tableSortDirection}
                    onClick={onTableSortColumnClick}
                  />
                  <ListViewSortableColumnHeader
                    label="Due date"
                    column="dueDate"
                    activeColumn={tableSortColumn}
                    direction={tableSortDirection}
                    onClick={onTableSortColumnClick}
                  />
                  <ListViewSortableColumnHeader
                    label="Priority"
                    column="priority"
                    activeColumn={tableSortColumn}
                    direction={tableSortDirection}
                    onClick={onTableSortColumnClick}
                  />
                  <ListViewSortableColumnHeader
                    label="Updated"
                    column="updatedAt"
                    activeColumn={tableSortColumn}
                    direction={tableSortDirection}
                    onClick={onTableSortColumnClick}
                  />
                  {visibleCustomFields.map((field) => (
                    <ListViewColumnHeader
                      key={field.id}
                      field={field}
                      onRemove={() => onRemoveColumn(field.id)}
                      canRemove={canManageColumns}
                      onRename={onRenameColumn}
                      canRename={canRenameColumn}
                    />
                  ))}
                  {showAddColumn ? (
                    <div className="flex items-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                            disabled={isAddingColumn}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add column
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[220px]">
                          {hiddenEffectiveFields.length > 0 ? (
                            hiddenEffectiveFields.map((field) => (
                              <DropdownMenuItem
                                key={field.id}
                                onClick={() => onAddExistingColumn?.(field.id)}
                              >
                                {field.name}
                              </DropdownMenuItem>
                            ))
                          ) : (
                            <DropdownMenuItem disabled>No hidden fields</DropdownMenuItem>
                          )}
                          {canCreateFieldDefinition && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => onCreateColumnByType?.('text')}>
                                + New text field
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => onCreateColumnByType?.('longText')}>
                                + New long text field
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => onCreateColumnByType?.('number')}>
                                + New number field
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <div className="w-8" />
                  )}
                </div>
              )}
              {tasks.map((task) => (
                <TaskListRow
                  key={task.id}
                  task={task}
                  onClick={() => onTaskClick(task.id)}
                  customColumns={visibleCustomFields}
                  customValues={customValuesByTask[task.id] ?? {}}
                  gridTemplateColumns={gridCols}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ListView({ tasks, statuses, isLoading, listId, projectId }: ViewProps) {
  const { openTaskPanel } = useUIStore();
  const [prefs, setPrefs] = useState<ListViewPreferences>(DEFAULT_LIST_VIEW_PREFS);
  const lastSavedPrefsRef = useRef(JSON.stringify(DEFAULT_LIST_VIEW_PREFS));
  const scopeType: 'list' | 'project' = listId ? 'list' : 'project';
  const scopeId = listId ?? projectId ?? null;
  const preferenceQuery = useMemo(
    () =>
      scopeId
        ? { scopeType, scopeId, viewType: 'list' as const }
        : null,
    [scopeId, scopeType],
  );
  const { data: savedPreference } = useViewPreference(preferenceQuery);
  const setViewPreference = useSetViewPreference();
  const clearViewPreference = useClearViewPreference();
  const { data: effectiveFields = [] } = useEffectiveCustomFieldsForList(listId ?? undefined);
  const { data: customValuesByTask = {} } = useTaskCustomFieldValuesForList(listId ?? undefined);
  const capabilities = useMyCapabilities().data;
  const canManageColumns = Boolean(capabilities?.['custom_fields.listview.columns.manage']);
  const canManageListCustomFields = Boolean(capabilities?.['custom_fields.list.manage']);
  const canManageProjectCustomFields = Boolean(capabilities?.['custom_fields.project.manage']);
  /** Rename updates a field definition — requires project or list definition manage (admin-only in matrix). */
  const canRenameColumn = canManageListCustomFields || canManageProjectCustomFields;

  useEffect(() => {
    const parsed = parseListViewPreferences(savedPreference?.preferences?.listView);
    setPrefs(parsed);
    lastSavedPrefsRef.current = JSON.stringify(parsed);
  }, [savedPreference?.preferences?.listView, scopeId]);

  // Debounced autosave (invisible UX; no success toast)
  useEffect(() => {
    if (!preferenceQuery) return;
    const serialized = JSON.stringify(prefs);
    if (serialized === lastSavedPrefsRef.current) return;
    const timer = window.setTimeout(() => {
      setViewPreference.mutate(
        {
          ...preferenceQuery,
          preferences: { listView: prefs },
        },
        {
          onSuccess: () => {
            lastSavedPrefsRef.current = serialized;
          },
          onError: () => {
            toast({ title: 'Could not save view preference', variant: 'destructive' });
          },
        },
      );
    }, 700);
    return () => window.clearTimeout(timer);
  }, [prefs, preferenceQuery, setViewPreference]);

  const groups = useMemo(
    () => applyListViewWithTableColumnSort(tasks, statuses, prefs),
    [tasks, statuses, prefs]
  );

  const handleTableSortColumnClick = (column: ListViewTableSortColumn) => {
    const next = nextPrefsFromTableSortClick(prefs, column);
    setPrefs(next);
  };

  const hasExplicitColumnPrefs = prefs.visibleCustomFieldIds.length > 0 || (prefs.hiddenCustomFieldIds ?? []).length > 0;

  const visibleCustomFields = useMemo(() => {
    if (!hasExplicitColumnPrefs) {
      return effectiveFields;
    }
    if (prefs.visibleCustomFieldIds.length > 0) {
      return prefs.visibleCustomFieldIds
        .map((id) => effectiveFields.find((f) => f.id === id))
        .filter((f): f is CustomFieldDefinition => !!f);
    }
    const hiddenIds = new Set(prefs.hiddenCustomFieldIds ?? []);
    return effectiveFields.filter((f) => !hiddenIds.has(f.id));
  }, [effectiveFields, prefs.visibleCustomFieldIds, prefs.hiddenCustomFieldIds, hasExplicitColumnPrefs]);

  const hiddenEffectiveFields = useMemo(() => {
    const visibleIds = new Set(visibleCustomFields.map((f) => f.id));
    return effectiveFields.filter((field) => !visibleIds.has(field.id));
  }, [effectiveFields, visibleCustomFields]);

  const showAddColumn = !!listId && canManageColumns;
  const createListField = useCreateListCustomField(listId ?? '');
  const updateCustomField = useUpdateCustomField();

  const nextKeyFor = (prefix: string): string => {
    const re = new RegExp(`^${prefix}_(\\d+)$`);
    const nums = effectiveFields
      .map((f) => f.key.match(re)?.[1])
      .filter(Boolean)
      .map(Number);
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    return `${prefix}_${next}`;
  };

  const handleAddExistingColumn = (fieldId: string) => {
    const next = {
      ...prefs,
      visibleCustomFieldIds: [],
      hiddenCustomFieldIds: (prefs.hiddenCustomFieldIds ?? []).filter((id) => id !== fieldId),
    };
    setPrefs(next);
  };

  const handleCreateColumnByType = (type: 'text' | 'longText' | 'number') => {
    if (!listId) return;
    const isLongText = type === 'longText';
    const payload =
      type === 'number'
        ? { name: 'Number', key: nextKeyFor('number'), type: 'number' as const }
        : isLongText
          ? { name: 'Long text', key: nextKeyFor('long_text'), type: 'text' as const, config: { longText: true } }
          : { name: 'Text', key: nextKeyFor('text'), type: 'text' as const };
    createListField.mutate(payload, {
      onSuccess: () => {
        const next = {
          ...prefs,
          visibleCustomFieldIds: [],
          hiddenCustomFieldIds: (prefs.hiddenCustomFieldIds ?? []).filter((id) => id !== ''),
        };
        setPrefs(next);
      },
      onError: () => {
        toast({ title: 'Could not add column', variant: 'destructive' });
      },
    });
  };

  const handleRenameColumn = (fieldId: string, newName: string) => {
    updateCustomField.mutate(
      { id: fieldId, data: { name: newName } },
      {
        onError: () => {
          toast({ title: 'Could not rename column', variant: 'destructive' });
        },
      }
    );
  };

  const handleRemoveColumn = (fieldId: string) => {
    const next = {
      ...prefs,
      visibleCustomFieldIds: [],
      hiddenCustomFieldIds: [...(prefs.hiddenCustomFieldIds ?? []).filter((id) => id !== fieldId), fieldId],
    };
    setPrefs(next);
  };

  const handleTaskClick = (taskId: string) => {
    openTaskPanel(taskId);
  };

  const handleToggleGroupExpanded = (groupKey: string) => {
    const collapsed = new Set(prefs.collapsedGroups);
    if (collapsed.has(groupKey)) {
      collapsed.delete(groupKey);
    } else {
      collapsed.add(groupKey);
    }
    setPrefs({
      ...prefs,
      collapsedGroups: Array.from(collapsed),
    });
  };

  const handleResetView = () => {
    setPrefs(DEFAULT_LIST_VIEW_PREFS);
    lastSavedPrefsRef.current = JSON.stringify(DEFAULT_LIST_VIEW_PREFS);
    if (!preferenceQuery) return;
    clearViewPreference.mutate(preferenceQuery, {
      onError: () => {
        toast({ title: 'Could not reset view preference', variant: 'destructive' });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (statuses.length === 0 && tasks.length > 0) {
    return (
      <div className="flex h-full flex-col">
        {listId && (
          <ListViewToolbar prefs={prefs} onPrefsChange={setPrefs} onReset={handleResetView} />
        )}
        <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground">
          <FolderOpen className="h-12 w-12 text-muted-foreground/30" />
          <p>No statuses configured</p>
        </div>
      </div>
    );
  }

  if (statuses.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <FolderOpen className="h-12 w-12 text-muted-foreground/30" />
        <p>No statuses configured</p>
        <p className="text-sm text-muted-foreground/70">
          Add statuses to organize your tasks
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col min-h-0">
      {listId && (
        <ListViewToolbar prefs={prefs} onPrefsChange={setPrefs} onReset={handleResetView} />
      )}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {tasks.length === 0 ? (
            <div className="flex flex-col gap-4">
              <ListViewGroupBlock
                groupKey="__empty__"
                label=""
                tasks={[]}
                onTaskClick={() => {}}
                visibleCustomFields={visibleCustomFields}
                customValuesByTask={{}}
                onRemoveColumn={handleRemoveColumn}
                canManageColumns={canManageColumns}
                onRenameColumn={handleRenameColumn}
                canRenameColumn={canRenameColumn}
                onAddExistingColumn={handleAddExistingColumn}
                hiddenEffectiveFields={hiddenEffectiveFields}
                onCreateColumnByType={handleCreateColumnByType}
                isAddingColumn={createListField.isPending}
                showAddColumn={showAddColumn}
                canCreateFieldDefinition={canManageListCustomFields}
                tableSortColumn={prefs.tableSortColumn}
                tableSortDirection={prefs.tableSortDirection}
                onTableSortColumnClick={handleTableSortColumnClick}
                isExpanded
                onToggleExpanded={handleToggleGroupExpanded}
              />
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground text-sm">
                No tasks in this list
              </div>
            </div>
          ) : (
            groups.map((group, idx) => (
              <ListViewGroupBlock
                key={`${group.key}-${idx}`}
                groupKey={group.key}
                label={group.label}
                tasks={group.tasks}
                onTaskClick={handleTaskClick}
                visibleCustomFields={visibleCustomFields}
                customValuesByTask={customValuesByTask}
                onRemoveColumn={handleRemoveColumn}
                canManageColumns={canManageColumns}
                onRenameColumn={handleRenameColumn}
                canRenameColumn={canRenameColumn}
                onAddExistingColumn={handleAddExistingColumn}
                hiddenEffectiveFields={hiddenEffectiveFields}
                onCreateColumnByType={handleCreateColumnByType}
                isAddingColumn={createListField.isPending}
                showAddColumn={showAddColumn}
                canCreateFieldDefinition={canManageListCustomFields}
                tableSortColumn={prefs.tableSortColumn}
                tableSortDirection={prefs.tableSortDirection}
                onTableSortColumnClick={handleTableSortColumnClick}
                isExpanded={!prefs.collapsedGroups.includes(group.key)}
                onToggleExpanded={handleToggleGroupExpanded}
                status={group.status}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

registerView({
  id: 'list',
  label: 'List',
  icon: 'List',
  component: ListView,
});

export { ListView };
